import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { FlexCenHorVer, FlexCol, FlexColCenVer } from '@/UI/styles';
import GalleryGradientOverlay from '@/components/Common/GalleryGradientOverlay';
import { useIsHovered } from '@/hooks/useIsHovered';
import { AssetOverlay, type AssetOverlayProps } from '@/components/Common/AssetOverlay/AssetOverlay';
import { CompactMediaPlayerControls } from '@/components/MediaPlayerControls/MediaPlayerControls';
import { PlayFilledIcon } from '@/UI/Icons/PlayFilledIcon';
import { LottieAnimation } from '@/components/Common/LottieAnimation';
import { MusicNoteIcon } from '@/UI/Icons/MusicNoteIcon';
import { TextViewer } from '@/components/Common/ImageList/TextViewer';
import { useAppContextMenu } from '@/UI/AppContextMenu/useAppContextMenu';
import {
  useEdges,
  useFlowView,
  useNodeDataNullable,
  useNodeRenderTarget,
  useVideoState,
  useVideoView,
} from '../../Recipe/FlowContext';
import { useAudioPlayback } from '../../Recipe/FlowComponents/Editor/Designer/useAudioPlayback';
import type { MouseEvent } from 'react';
import type { MenuAction } from '@/components/Menu/Actions';
import type { FileKind, NodeId } from 'web';
import type { UploadedAsset } from '@/types/api/assets';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';

function VideoViewer({
  id,
  handleContextMenu,
  onVideoPause,
  onVideoScrub,
  disabled,
  clickToPlayDisabled,
  overlay,
  index = 0,
  width,
  height,
  duration,
  hasAudio,
  fps,
  shouldPlayOnHover,
  isHovered,
  noBorderRadius,
}: {
  id: NodeId;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  onVideoPause?: (time: number) => void;
  onVideoScrub?: (time: number, ongoing: boolean) => void;
  disabled?: boolean;
  clickToPlayDisabled?: boolean;
  overlay?: React.ReactNode;
  index?: number;
  width: number;
  height: number;
  duration: number;
  hasAudio: boolean;
  fps?: number;
  shouldPlayOnHover?: boolean;
  isHovered?: boolean;
  noBorderRadius?: boolean;
}) {
  const canvasRef = useNodeRenderTarget(id, index);
  const videoView = useVideoView(id, index);
  const { paused, currentTime, muted } = useVideoState(id, index) ?? {
    paused: true,
    currentTime: 0,
    muted: true,
  };

  // Sync audio layers from upstream compositor with video playback
  const edges = useEdges();
  const sourceCompositorId = useMemo(() => {
    const edge = edges.find((e) => e.target === id);
    return edge?.source;
  }, [edges, id]);
  const sourceData = useNodeDataNullable<CompositorNodeV3>(sourceCompositorId);

  useAudioPlayback({
    layers: sourceData?.data?.layers,
    layerOrder: sourceData?.data?.layerOrder,
    compositorInputs: sourceData?.data?.input,
    currentTime,
    isPlaying: !paused,
    globalMuted: muted,
  });

  // Handle video play on hover when shouldPlayOnHover is enabled
  useEffect(() => {
    if (!shouldPlayOnHover) {
      return;
    }
    if (isHovered && paused) {
      videoView.togglePlay();
    } else if (!isHovered && !paused) {
      videoView.togglePlay();
    }
  }, [isHovered, shouldPlayOnHover, paused, videoView]);

  const handleTogglePlay = useCallback(() => {
    if (disabled) return;
    if (!paused) {
      onVideoPause?.(currentTime);
    }
    videoView.togglePlay();
  }, [onVideoPause, videoView, paused, currentTime, disabled]);

  const handleToggleMute = useCallback(() => {
    if (disabled) return;
    videoView.toggleMute();
  }, [videoView, disabled]);

  const handleScrub = useCallback(
    (time: number, ongoing: boolean) => {
      if (disabled) return;
      if (!paused) {
        videoView.togglePlay();
      }
      onVideoScrub?.(time, ongoing);
      videoView.setTime(time, ongoing);
    },
    [onVideoScrub, videoView, paused, disabled],
  );

  return (
    <FlexColCenVer sx={{ gap: 2 }}>
      <FlexCenHorVer
        className="media-container-dark"
        onContextMenu={handleContextMenu}
        onClick={shouldPlayOnHover || clickToPlayDisabled ? undefined : handleTogglePlay}
        sx={{
          cursor: clickToPlayDisabled ? undefined : 'pointer',
          position: 'relative',
          borderRadius: noBorderRadius ? 0 : 2,
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={canvasRef}
          draggable="false"
          style={{
            width: '100%',
            aspectRatio: `${width}/${height}`,
            position: 'relative',
          }}
        />
        {overlay}
        {shouldPlayOnHover && !isHovered && (
          <Box
            sx={{
              position: 'absolute',
            }}
          >
            <PlayFilledIcon />
          </Box>
        )}
      </FlexCenHorVer>

      {!shouldPlayOnHover && (
        <FlexColCenVer sx={{ gap: 1 }}>
          <CompactMediaPlayerControls
            currentTime={currentTime}
            duration={duration}
            fps={fps}
            isPaused={paused}
            isMuted={muted}
            hasAudio={hasAudio}
            onTogglePlay={handleTogglePlay}
            onSetTime={handleScrub}
            onToggleMute={handleToggleMute}
          />
        </FlexColCenVer>
      )}
    </FlexColCenVer>
  );
}

export function ThreeDViewer({
  id,
  index,
  handleContextMenu,
  overlay,
  allowInteraction = true,
}: {
  id: NodeId;
  index?: number;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  overlay?: React.ReactNode;
  allowInteraction?: boolean;
}) {
  const canvasRef = useNodeRenderTarget(id, index ?? 0);

  return (
    <FlexCenHorVer
      className="media-container-dark nodrag nopan nowheel"
      onContextMenu={handleContextMenu}
      sx={{ borderRadius: 2, overflow: 'hidden', pointerEvents: allowInteraction ? 'auto' : 'none' }}
    >
      <canvas
        ref={canvasRef}
        draggable="false"
        style={{
          width: '100%',
          aspectRatio: '1',
          position: 'relative',
        }}
      />
      {overlay}
    </FlexCenHorVer>
  );
}

export function ImageViewer({
  id,
  width,
  height,
  index,
  handleContextMenu,
  overlay,
  noBorderRadius,
}: {
  id: NodeId;
  width: number;
  height: number;
  index?: number;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  overlay?: React.ReactNode;
  noBorderRadius?: boolean;
}) {
  const canvasRef = useNodeRenderTarget(id, index ?? 0);

  return (
    <FlexCenHorVer
      className="media-container-dark"
      onContextMenu={handleContextMenu}
      sx={{ borderRadius: noBorderRadius ? 0 : 2, overflow: 'hidden' }}
    >
      <canvas
        ref={canvasRef}
        draggable="false"
        style={{
          width: '100%',
          aspectRatio: `${width}/${height}`,
          position: 'relative',
        }}
      />
      {overlay}
    </FlexCenHorVer>
  );
}

export function ImageFileViewer({
  url,
  thumbnailUrl,
  width,
  height,
  handleContextMenu,
  overlay,
}: {
  url: string;
  thumbnailUrl?: string;
  width: number;
  height: number;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  overlay?: React.ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(true);
  return (
    <FlexCenHorVer
      className="media-container-dark"
      onContextMenu={handleContextMenu}
      sx={{ borderRadius: 2, overflow: 'hidden' }}
    >
      {thumbnailUrl && isLoading ? (
        <img
          draggable="false"
          src={thumbnailUrl}
          style={{
            width: '100%',
            aspectRatio: `${width}/${height}`,
            position: 'relative',
          }}
        />
      ) : (
        <></>
      )}
      <img
        hidden={isLoading && thumbnailUrl !== undefined}
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
        draggable="false"
        src={url}
        style={{
          width: '100%',
          aspectRatio: `${width}/${height}`,
          position: 'relative',
        }}
      />
      {overlay}
    </FlexCenHorVer>
  );
}

function AudioPreview({
  url,
  overlay,
  playOnHover,
  isHovered,
  fixedItemSize,
}: {
  url: string;
  overlay?: React.ReactNode;
  playOnHover?: boolean;
  isHovered?: boolean;
  fixedItemSize?: number;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  // Play/Pause toggle
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Jump to specific time (in seconds)
  const setAudioTime = (time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = time;
    setCurrentTime(time);
  };

  // Update current time as audio plays
  const handleTimeUpdate = () => {
    setCurrentTime(audioRef.current?.currentTime ?? 0);
  };

  // Set duration when metadata loads
  const handleLoadedMetadata = () => {
    setDuration(audioRef.current?.duration ?? 0);
  };

  const toggleMuted = () => {
    if (!audioRef.current) return;
    setMuted(!muted);
    audioRef.current.muted = !muted;
  };

  function handleEnded(): void {
    setIsPlaying(false);
    setAudioTime(0);
  }
  // handle audio play on hover
  useEffect(() => {
    if (!playOnHover || !audioRef.current) {
      return;
    }
    if (isHovered) {
      void audioRef.current.play();
    } else {
      void audioRef.current.pause();
    }
  }, [playOnHover, isHovered]);

  return (
    <FlexColCenVer sx={{ gap: 2 }}>
      {!playOnHover ? (
        <>
          <Box
            className="media-container-dark"
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <img src="/audio.png" alt="Audio" style={{ display: 'block', width: '100%', mixBlendMode: 'multiply' }} />
            <audio
              ref={audioRef}
              draggable="false"
              crossOrigin="anonymous"
              src={url}
              onEnded={handleEnded}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              style={{
                display: 'block',
                width: '80%',
                position: 'absolute',
                top: `calc(50% - 30px)`,
                left: `calc(50% - 40%)`,
              }}
            />
            {overlay}
          </Box>
          <CompactMediaPlayerControls
            currentTime={currentTime}
            duration={duration}
            isPaused={!isPlaying}
            isMuted={muted}
            hasAudio={true}
            onTogglePlay={togglePlayPause}
            onSetTime={setAudioTime}
            onToggleMute={toggleMuted}
          />
        </>
      ) : (
        <Box
          sx={{
            height: fixedItemSize ? `${fixedItemSize}px` : '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <audio ref={audioRef} draggable="false" crossOrigin="anonymous" src={url} loop={true} />
          {isHovered ? (
            <LottieAnimation width={42} height={42} loop={true} src="/audio-wave.lottie" />
          ) : (
            <MusicNoteIcon />
          )}
        </Box>
      )}
    </FlexColCenVer>
  );
}

export interface FileViewerProps {
  id: NodeId;
  index?: number;
  asset?: UploadedAsset | FileKind;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  isClickable?: boolean;
  isMultiFilesViewMode?: boolean;
  fixedItemSize?: number;
  onTextChange?: (index: number, value: string) => void;
  hasMultipleFiles?: boolean;
}

export function FileViewer({
  id,
  index,
  asset,
  handleContextMenu,
  isClickable,
  isMultiFilesViewMode = false,
  hasMultipleFiles = false,
  fixedItemSize,
  onTextChange,
}: FileViewerProps) {
  const { isHovered, ...hoveredProps } = useIsHovered();
  const overlay = null;

  return (
    <FlexCol
      {...hoveredProps}
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        cursor: isClickable ? 'pointer' : 'default',
      }}
      className="nodrag"
    >
      {asset?.type === 'video' ? (
        <VideoViewer
          id={id}
          isHovered={isHovered}
          index={index}
          handleContextMenu={handleContextMenu}
          overlay={overlay}
          disabled={false}
          width={asset.width ?? 1}
          height={asset.height ?? 1}
          duration={asset.duration ?? 0}
          hasAudio={asset.hasAudio ?? true}
          fps={asset.fps}
          shouldPlayOnHover={isMultiFilesViewMode}
        />
      ) : asset?.type === 'image' ? (
        <ImageFileViewer
          key={asset.url}
          url={asset.url}
          thumbnailUrl={asset.thumbnailUrl}
          width={asset.width ?? 1}
          height={asset.height ?? 1}
          handleContextMenu={handleContextMenu}
          overlay={overlay}
        />
      ) : asset?.type === '3D' ? (
        <ThreeDViewer
          id={id}
          index={index}
          handleContextMenu={handleContextMenu}
          overlay={overlay}
          allowInteraction={!isMultiFilesViewMode}
        />
      ) : asset?.type === 'audio' ? (
        <AudioPreview
          url={asset.url}
          overlay={overlay}
          playOnHover={isMultiFilesViewMode}
          isHovered={isHovered}
          fixedItemSize={fixedItemSize}
        />
      ) : asset?.type === 'text' ? (
        <TextViewer
          file={asset}
          index={index ?? 0}
          isMultiFilesViewMode={isMultiFilesViewMode}
          hasMultipleFiles={hasMultipleFiles}
          fixedItemSize={fixedItemSize}
          onTextChange={onTextChange}
        />
      ) : (
        <FlexCol className="media-container-dark" sx={{ width: '100%', height: '430px' }} />
      )}
    </FlexCol>
  );
}

interface NodeViewerProps {
  id: NodeId;
  contextMenuItems?: MenuAction[];
  onVideoPause?: (time: number) => void;
  onVideoScrub?: (time: number, ongoing: boolean) => void;
  disabled?: boolean;
  clickToPlayDisabled?: boolean;
  cursor?: string;
  noBorderRadius?: boolean;
}

function ComputedAudioViewer({ id, duration, overlay }: { id: NodeId; duration: number; overlay?: React.ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [muted, setMuted] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef(0);

  // Find source compositor for audio layer data
  const edges = useEdges();
  const sourceCompositorId = useMemo(() => {
    const edge = edges.find((e) => e.target === id);
    return edge?.source;
  }, [edges, id]);
  const sourceData = useNodeDataNullable<CompositorNodeV3>(sourceCompositorId);

  useAudioPlayback({
    layers: sourceData?.data?.layers,
    layerOrder: sourceData?.data?.layerOrder,
    compositorInputs: sourceData?.data?.input,
    currentTime,
    isPlaying,
    globalMuted: muted,
  });

  // Advance currentTime while playing
  useEffect(() => {
    if (isPlaying) {
      startRef.current = performance.now() - currentTime * 1000;
      intervalRef.current = setInterval(() => {
        const elapsed = (performance.now() - startRef.current) / 1000;
        if (elapsed >= duration) {
          setCurrentTime(0);
          setIsPlaying(false);
        } else {
          setCurrentTime(elapsed);
        }
      }, 50);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // currentTime intentionally excluded — including it would restart the interval every 50ms.
    // Scrubbing updates startRef directly via handleScrub instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, duration]);

  const handleTogglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleToggleMute = useCallback(() => {
    setMuted((prev) => !prev);
  }, []);

  const handleScrub = useCallback((time: number) => {
    setCurrentTime(time);
    startRef.current = performance.now() - time * 1000;
  }, []);

  return (
    <FlexColCenVer sx={{ gap: 2 }}>
      <FlexCenHorVer
        className="media-container-dark"
        sx={{
          width: '100%',
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <img src="/audio.png" alt="Audio" style={{ display: 'block', width: '100%', mixBlendMode: 'multiply' }} />
        {overlay}
      </FlexCenHorVer>
      <CompactMediaPlayerControls
        currentTime={currentTime}
        duration={duration}
        isPaused={!isPlaying}
        isMuted={muted}
        hasAudio={true}
        onTogglePlay={handleTogglePlay}
        onSetTime={handleScrub}
        onToggleMute={handleToggleMute}
      />
    </FlexColCenVer>
  );
}

export function NodeViewer({
  id,
  contextMenuItems,
  onVideoPause,
  onVideoScrub,
  disabled,
  clickToPlayDisabled,
  noBorderRadius,
  cursor = 'default',
  ...overlayProps
}: NodeViewerProps) {
  const flowView = useFlowView();
  const file = flowView.evaluateNode(id);
  const { isHovered, ...hoveredProps } = useIsHovered();
  const hasContextMenuItems = (contextMenuItems?.length ?? 0) > 0;
  const { handleContextMenuOpen, contextMenu } = useAppContextMenu({
    items: contextMenuItems ?? [],
  });
  const handleContextMenu = hasContextMenuItems ? handleContextMenuOpen : undefined;

  const overlay = useMemo(() => {
    return file?.type === 'image' || file?.type === 'video' || file?.type === '3D' || file?.type === 'audio' ? (
      <Overlay {...overlayProps} asset={{ ...file.value, type: file.type } as UploadedAsset} isHovered={isHovered} />
    ) : null;
  }, [overlayProps, file, isHovered]);

  return (
    <Box {...hoveredProps} sx={{ width: '100%', height: '100%', cursor, position: 'relative' }} className="nodrag">
      {file?.type === 'video' ? (
        <VideoViewer
          id={id}
          disabled={disabled}
          clickToPlayDisabled={clickToPlayDisabled}
          overlay={overlay}
          handleContextMenu={handleContextMenu}
          onVideoPause={onVideoPause}
          onVideoScrub={onVideoScrub}
          width={file.value.width}
          height={file.value.height}
          duration={file.value.duration}
          fps={file.value.fps}
          hasAudio={file.value.hasAudio ?? true}
          noBorderRadius={noBorderRadius}
        />
      ) : file?.type === 'image' ? (
        <ImageViewer
          id={id}
          width={file.value.width}
          height={file.value.height}
          handleContextMenu={handleContextMenu}
          overlay={overlay}
          noBorderRadius={noBorderRadius}
        />
      ) : file?.type === '3D' ? (
        <ThreeDViewer id={id} handleContextMenu={handleContextMenu} overlay={overlay} />
      ) : file?.type === 'audio' && 'type' in file.value && file.value.type === 'computed' ? (
        <ComputedAudioViewer id={id} duration={file.value.duration} overlay={overlay} />
      ) : file?.type === 'audio' && 'url' in file.value ? (
        <AudioPreview url={(file.value as { url: string }).url} overlay={overlay} />
      ) : (
        <Box className="media-container-dark" sx={{ width: '100%', height: '430px' }} />
      )}
      {hasContextMenuItems ? contextMenu : null}
    </Box>
  );
}

function Overlay(props: AssetOverlayProps) {
  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, pointerEvents: 'none' }}>
      <GalleryGradientOverlay isSelected={props.isHovered} />
      <AssetOverlay isHovered={props.isHovered} {...props} />
    </Box>
  );
}
